import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import SchoolRow from './SchoolRow';
import AdminIcon from './AdminIcon';
import { adminServices } from '../services/adminServices'; // <--- ADD THIS

const SchoolDirectory = ({
  onEditReport,
  onOpenDossier,
  showToast,
  initialExpandedId,
  permissions = {},
  currentUserUid = null,
}) => {
  const [schools, setSchools] = useState([]);
  const [viewMode, setViewMode] = useState('geo'); // 'geo' or 'actions'
  const [searchTerm, setSearchTerm] = useState('');
  /** Geographic view: at most one country section expanded at a time. */
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    // Fetch all schools. We use Name as the base sort.
    const q = query(collection(db, "schools"), orderBy("officialName", "asc"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const schoolData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setSchools(schoolData);
    });

    return () => unsubscribe();
  }, []);

  // Deep-link / task context: expand this row once when `initialExpandedId` changes — not on every
  // Firestore `schools` snapshot (that was resetting `expandedId` and breaking single-row accordion UX).
  useEffect(() => {
    if (!initialExpandedId) return;
    setExpandedId(initialExpandedId);
  }, [initialExpandedId]);

  // Open the country accordion in geo view so the deep-linked school is visible (idempotent).
  useEffect(() => {
    if (!initialExpandedId || viewMode !== 'geo' || schools.length === 0) return;
    const targetSchool = schools.find((s) => s.id === initialExpandedId);
    if (!targetSchool) return;
    const country = targetSchool.answers?.officialAddressCountry || 'NOT SET';
    setExpandedCountry(country);
  }, [initialExpandedId, viewMode, schools]);

  // 1. MASTER FILTER & SORT LOGIC
  const getProcessedSchools = () => {
    const term = searchTerm.trim().toLowerCase();
    const canViewAll = permissions.canViewAllSchools === true;
    const paidSchools = schools.filter((s) => {
      if (s.paymentStatus !== 'paid' && s.paymentStatus !== 'awaiting_external_settlement') {
        return false;
      }
      // Lead accreditors without canViewAllSchools only see schools assigned to them.
      if (!canViewAll) {
        return !!currentUserUid && s.assignedAuditorUid === currentUserUid;
      }
      return true;
    });

    // SCENARIO A: SEARCH OVERRIDE
    if (term.length > 0) {
      return paidSchools.filter(s => { // Changed from schools.filter
        const name = s.officialName?.toLowerCase() || "";
        const country = s.answers?.officialAddressCountry?.toLowerCase() || "";
        return name.includes(term) || country.includes(term);
      });
    }

    // SCENARIO B: ACTION REQUIRED — same red queue as getAuditTurn(school); bucket/sort via getRegistryActionBucket
    if (viewMode === 'actions') {
      const red = paidSchools.filter((s) => adminServices.getAuditTurn(s, null, null).priority === 1);
      return red.sort((a, b) => {
        const bo = adminServices.getRegistryActionBucket(a).order - adminServices.getRegistryActionBucket(b).order;
        if (bo !== 0) return bo;
        return (b.lastWorkflowUpdate?.seconds || 0) - (a.lastWorkflowUpdate?.seconds || 0);
      });
    }

    // SCENARIO C: GEOGRAPHIC VIEW (Default)
    return [...paidSchools].sort((a, b) => { // Changed from [...schools]
      const countryA = a.answers?.officialAddressCountry || "ZZZ";
      const countryB = b.answers?.officialAddressCountry || "ZZZ";
      return countryA.localeCompare(countryB);
    });
  };

  const toggleCountryAccordion = (country) => {
    setExpandedCountry((prev) => (prev === country ? null : country));
  };

  const processedSchools = getProcessedSchools();
  const isSearching = searchTerm.trim().length > 0;
  let lastGroupHeader = null;

  /** Accordion: at most one expanded row; functional update avoids stale closures. */
  const rowToggle = (schoolId) => () => {
    setExpandedId((prev) => (prev === schoolId ? null : schoolId));
  };

  return (
    <div className="registry-container">
      
      {/* --- TOP VIEW CONTROLS --- */}
      <div className="view-switcher" style={{ marginBottom: '30px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button 
          onClick={() => {
            setViewMode('geo');
            setExpandedCountry(null);
          }} 
          className={`view-btn ${viewMode === 'geo' ? 'active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <AdminIcon name="geo" fallback="🗺️" size="18px" /> Geographic Accordion
        </button>
        
        <button 
          onClick={() => {
            setViewMode('actions');
            setExpandedCountry(null);
          }} 
          className={`view-btn ${viewMode === 'actions' ? 'active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <AdminIcon name="action" fallback="⚡" size="18px" /> Action Required
        </button>

        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'absolute', left: '15px' }}>
            <AdminIcon name="search" fallback="🔍" size="14px" style={{ opacity: 0.5 }} />
          </div>
          <input 
            type="text" 
            placeholder="Search by school name or country..." 
            className="admin-search" 
            style={{ margin: 0, width: '100%', paddingLeft: '45px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="stat-pill" style={{ whiteSpace: 'nowrap' }}>
          {processedSchools.length} Results
        </div>
      </div>

      <div className="school-table-header">
        <div className="col-name">Institution</div>
        <div className="col-level">Stage</div>
        <div className="col-status">SEF Access</div>
        <div className="col-progress">Progress</div>
        <div className="col-audit-status">Turn status</div>
        <div className="col-action"></div>
      </div>

      <div className="school-list">
        {processedSchools.map((school) => {
          // Define current country once at the top of the map
          const country = school.answers?.officialAddressCountry || "NOT SET";

          // CASE 1: SEARCHING (Show all matches in a flat list)
          if (isSearching) {
            return <SchoolRow 
            key={school.id} 
            school={school} 
            onEditReport={onEditReport} 
            onOpenDossier={onOpenDossier}
            showToast={showToast}
            isOpen={expandedId === school.id}
            onToggle={rowToggle(school.id)}
          />
          }

           // CASE 2: ACTION REQUIRED VIEW (bucket headers aligned with getAuditTurn / getRegistryActionBucket)
          if (viewMode === 'actions') {
            const bucket = adminServices.getRegistryActionBucket(school).header;
            const showBucketHeader = bucket !== lastGroupHeader;
            lastGroupHeader = bucket;

            return (
              <React.Fragment key={school.id}>
                {showBucketHeader && (
                  <div className="action-group-header" style={{ 
                    background: 'rgba(209, 29, 126, 0.15)', 
                    borderLeft: '4px solid #d11d7e',
                    color: '#d11d7e', padding: '10px 20px', fontSize: '0.7rem',
                    fontWeight: 'bold', margin: '30px 0 10px 0', borderRadius: '4px'
                  }}>
                    ⚡ {bucket.toUpperCase()}
                  </div>
                )}
                <SchoolRow 
                  school={school} 
                  onEditReport={onEditReport} 
                  onOpenDossier={onOpenDossier}
                  showToast={showToast}
                  isOpen={expandedId === school.id}
                  onToggle={rowToggle(school.id)}
                />
              </React.Fragment>
            );
          }

          // CASE 3: GEOGRAPHIC VIEW (Country Accordions)
          const showGeoHeader = country !== lastGroupHeader;
          lastGroupHeader = country;
          const isAccordionOpen = expandedCountry === country;

          return (
            <React.Fragment key={school.id}>
              {showGeoHeader && (
                <div className="country-group-header accordion" onClick={() => toggleCountryAccordion(country)}>
                  <span style={{ 
                    transition: 'transform 0.2s', 
                    display: 'inline-block', 
                    transform: isAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)' 
                  }}>
                    ▶
                  </span> 
                  {country.toUpperCase()}
                </div>
              )}
              {isAccordionOpen && (
                <SchoolRow 
                  school={school} 
                  onEditReport={onEditReport} 
                  onOpenDossier={onOpenDossier}
                  showToast={showToast}
                  isOpen={expandedId === school.id}
                  onToggle={rowToggle(school.id)}
                />
              )}
            </React.Fragment>
          );
        })}

        {processedSchools.length === 0 && (
          <div style={{ textAlign: 'center', padding: '50px', opacity: 0.5 }}>
            No institutions found matching your current filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolDirectory;